import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { listConceptSessions, listPurchaseSessions, registerSeat, cancelRegistration, listSeats } from '../api';

// Register a paid seat into an open session. Lists via conceptId (concept class)
// or purchaseId (catalog class). If seatId is given the seat is fixed (a seat
// holder scheduling their own seat); otherwise the buyer picks from their seats.
export default function SessionPicker({ conceptId, purchaseId, seatId, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [seats, setSeats] = useState([]);
  const [pickedSeat, setPickedSeat] = useState(seatId ? String(seatId) : '');
  const [busy, setBusy] = useState(false);

  const loadSessions = () => (conceptId ? listConceptSessions(conceptId) : listPurchaseSessions(purchaseId))
    .then((r) => setSessions(r.sessions || [])).catch(() => toast.error('Could not load sessions'));

  useEffect(() => {
    loadSessions();
    if (!seatId && purchaseId) listSeats(purchaseId).then((r) => setSeats(r.seats || [])).catch(() => {});
  }, [conceptId, purchaseId, seatId]);

  const register = async (sessionId) => {
    if (!pickedSeat) { toast.error('Pick a seat first'); return; }
    setBusy(true);
    try {
      await registerSeat(sessionId, pickedSeat);
      toast.success('Registered. A calendar invite is on its way.');
      onClose?.(true);
    } catch (e) {
      toast.error(e?.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  const cancelReg = async (id) => {
    try { await cancelRegistration(id); toast.success('Registration canceled'); onClose?.(true); }
    catch (e) { toast.error(e?.message || 'Cancel failed'); }
  };

  return (
    <div className="mt-3 rounded-xl border border-blue-100 dark:border-gray-700 p-4 space-y-3">
      <h3 className="text-sm font-bold text-blue-900 dark:text-blue-200">Schedule this class</h3>

      {!seatId && seats.length > 0 && (
        <label className="block text-sm">
          <span className="text-gray-500">Seat to register</span>
          <select value={pickedSeat} onChange={(e) => setPickedSeat(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm">
            <option value="">Select a seat...</option>
            {seats.map((s) => <option key={s.id} value={s.id}>{s.user || s.invited_email || `Seat ${s.id}`}</option>)}
          </select>
        </label>
      )}
      {!seatId && seats.length === 0 && (
        <p className="text-xs text-gray-500">Assign or claim a seat for this class first, then you can register it for a session.</p>
      )}

      {sessions.length === 0 && <p className="text-sm text-gray-500">No sessions scheduled yet. Check back soon.</p>}
      <div className="space-y-2">
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <div>
              <div className="text-sm font-semibold">{new Date(s.starts_at).toLocaleString([], { timeZone: s.time_zone })}</div>
              <div className="text-xs text-gray-500">
                {s.modality}{s.instructor ? ` · ${s.instructor.name}` : ''} · {s.seats_available} seats left
              </div>
            </div>
            {s.my_registration_id ? (
              <button onClick={() => cancelReg(s.my_registration_id)}
                className="text-sm font-semibold px-4 py-1.5 rounded-lg border border-orange-400 text-orange-600">
                Cancel
              </button>
            ) : (
              <button disabled={busy || s.seats_available <= 0} onClick={() => register(s.id)}
                className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-50">
                Register
              </button>
            )}
          </div>
        ))}
      </div>

      <button onClick={() => onClose?.(false)} className="text-sm text-gray-500">Close</button>
    </div>
  );
}
